/**
 * Publication Status Changed Renderer
 *
 * Displays status change batch process details:
 * operation description, target status badge, and total publications count.
 *
 * Data shape (from PublicationStatusChangedBatchProcessStartedEventSchema):
 *   publicationsIds: string[]
 *   statusToPublish: 'active' | 'paused'
 */

import { FC } from 'react';
import { Badge, Group, Stack, Text } from '@mantine/core';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

const DetailComponent: FC<DetailRendererProps> = ({ process, t }) => {
  const data = ( process.data || {}) as ProcessData;
  const statusToPublish = data.statusToPublish || 'active';
  const publicationsCount = data.publicationsIds?.length ?? process.totalItems ?? 0;
  const statusLabel = t( `batchProcesses_renderer_${statusToPublish}` );

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t( 'batchProcesses_operations_PublicationStatusChanged_description' )}
      </Text>

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_statusToPublish' )}:
        </Text>
        <Badge
          size="sm"
          color={statusToPublish === 'active' ? 'primary' : 'yellow'}
          variant="light"
        >
          {statusLabel}
        </Badge>
      </Group>

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_publicationCount' )}:
        </Text>
        <Text fz="sm">
          {publicationsCount}
        </Text>
      </Group>
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t ) => {
    const statusToPublish = data?.statusToPublish || 'active';
    return t( 'batchProcesses_summary_toStatus', {
      status: t( `batchProcesses_renderer_${statusToPublish}` )
    });
  },
  DetailComponent,
  getRequiredReferenceIds: ( process ) => {
    const data = ( process?.data || {}) as ProcessData;
    return {
      publicationIds: data.publicationsIds || []
    };
  }
};

export default DetailComponent;
